import { CartItem, DiscountAllocation } from '../types'
import { convertPriceToCents } from './priceFormat'

/**
 * Returns all of the applicable discount code related discount allocations for a line item
 * @returns DiscountCodeAllocation[]
 */
export function getItemAppliedDiscountCodeAllocations(item: CartItem) {
    return (
        item.discountAllocations?.filter(
            (allocation) =>
                allocation.targetType === 'LINE_ITEM' && !!allocation.code,
        ) ?? []
    )
}

/** Automatic-discount allocations carry a title but no code. */
export function getItemAutomaticDiscountAllocations(item: CartItem) {
    return (
        item.discountAllocations?.filter(
            (allocation) =>
                allocation.targetType === 'LINE_ITEM' && !allocation.code,
        ) ?? []
    )
}

/**
 * Returns all of the applied discount codes on a specific line item.
 * @returns string[]
 */
export function getItemAppliedDiscountCodes(item: CartItem): string[] {
    return getItemAppliedDiscountCodeAllocations(item)
        .map(({ code }) => code)
        .filter(
            (value): value is string =>
                !!value && typeof value === 'string' && value.trim().length > 0,
        )
        .map((code) => code.trim())
}

/**
 * Whether the line item already has a discount code applied to it.
 */
export function hasAppliedDiscountCode(item: CartItem): boolean {
    return getItemAppliedDiscountCodes(item).length > 0
}

function sumAllocationSavings(allocations: DiscountAllocation[]): number {
    return allocations.reduce((sum, allocation) => {
        const discountAmount = convertPriceToCents(
            allocation.discountedAmount.amount,
        )
        if (discountAmount === null || discountAmount === undefined) {
            console.warn(
                `Failed to convert discount amount to cents: ${allocation.discountedAmount.amount}`,
                { allocation },
            )
            return sum
        }
        return sum + discountAmount
    }, 0)
}

/** Discount-code savings on a line item, inclusive of quantity. */
export function calculateItemDiscountCodeSavings(item: CartItem): number {
    return sumAllocationSavings(getItemAppliedDiscountCodeAllocations(item))
}

/** Automatic-discount savings on a line item, inclusive of quantity. */
export function calculateItemAutomaticDiscountSavings(item: CartItem): number {
    return sumAllocationSavings(getItemAutomaticDiscountAllocations(item))
}
